<template>
  <ui-modal-window
    class="emh-side-filters-modal"
    :show="show"
    mode="edit-mobile-fullscreen"
    data-test-id="emh-side-filters-modal"
    :scroll-lock="false"
    @close="closeModal"
  >
    <wb-modal-header>
      <button
        v-if="selectedCategoryLevel"
        class="emh-side-filters-modal__back-navigation wb-button wb-button--tertiary wb-button--small"
        data-test-id="emh-side-filters-modal__back-navigation"
        :disabled="isLoading"
        @click="onSelectSubCategory()"
      >
        <wb-icon class="wb-button__icon" :name.attr="'bds/arrow-left/24'" aria-hidden="true" icon-flip-rtl />
        {{ $t('dcp.platform.button.navigation.back') }}
      </button>
    </wb-modal-header>

    <wb-modal-content><slot name="content"></slot></wb-modal-content>
    <wb-modal-footer :class="{ '-selected': isAnyFilterSelected }">
      <button
        v-if="isAnyFilterSelected"
        class="wb-button wb-button--secondary wb-button--large"
        type="button"
        :disabled="isLoading"
        @click="onClearAllFilters"
      >
        {{ `${$t('dcp.assortment.cars.filtersSearch.filters.reset')} (${selectedFiltersCount})` }}
      </button>
      <button
        class="wb-button wb-button--primary wb-button--large"
        type="button"
        data-test-id="emh-side-filters-modal__show-cars-button"
        @click="closeModal"
      >
        <wb-icon class="wb-button__icon" :name.attr="'bds/car/24'" aria-hidden="true" flip-rtl></wb-icon>
        {{
          `${$t('dcp.assortment.cars.filtersSearch.show')} (${totalResultsLimited})
           ${$t('dcp.assortment.cars.filtersSearch.cars')}`
        }}
      </button>
    </wb-modal-footer>
  </ui-modal-window>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRefs } from 'vue';
import UiModalWindow from '@dh-io-emh/ui/src/components/modalWindow/ModalWindow.vue';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';

export default defineComponent({
  name: 'EmhSideFiltersModal',
  components: { UiModalWindow },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      required: true,
    },
    selectedFilters: {
      type: Array as PropType<IProductFilterType[]>,
      default: () => [],
    },
    selectedCategoryLevel: {
      type: String,
      default: '',
    },
  },

  emits: ['clear-all-filters', 'close', 'select-subcategory'],

  setup(props, { emit }) {
    const productsStore = useProductsStore();
    const { totalResultsLimited } = toRefs(productsStore.selectors);
    const { selectedFilters } = toRefs(props);

    const selectedFiltersCount = computed(() => {
      return selectedFilters.value?.reduce((acc, filters) => {
        return acc + filters.value?.length;
      }, 0);
    });
    const isAnyFilterSelected = computed(() => selectedFiltersCount.value > 0);
    const onClearAllFilters = () => emit('clear-all-filters');

    const closeModal = () => {
      emit('close');
    };

    const onSelectSubCategory = (category?: string) => {
      emit('select-subcategory', category);
    };

    return {
      onClearAllFilters,
      isAnyFilterSelected,
      selectedFiltersCount,
      totalResultsLimited,
      onSelectSubCategory,
      closeModal,
    };
  },
});
</script>

<style lang="scss" scoped>
// fix icon not being displayed due to wb-modal styles
.wb-button__icon :not(:last-child) {
  display: inline;
}

wb-modal-footer {
  @include breakpoint-to(mq2) {
    &.-selected {
      .wb-button--secondary {
        display: inline;
      }
    }
  }
  @include breakpoint-between(mq3, mq5) {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: var(--wb-grid-gutter-width);

    &.-selected {
      grid-template-columns: repeat(2, 1fr);
    }

    & .wb-button:not(:last-child) {
      display: grid;
      margin: 0;
    }
  }
}
</style>
